import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
    ChevronRight,
    ChevronDown,
    PanelLeftClose,
    PanelLeft,
    Building2,
    Database,
    Activity,
    Users,
    Settings,
    Shield
} from 'lucide-react';
import { useSidebar } from '../../../context/SidebarContext';

/**
 * SuperAdminSidebar Component
 *
 * A collapsible sidebar navigation for the super admin panel,
 * matching the AdminSidebar styling and behavior.
 */
export default function SuperAdminSidebar() {
    const { isCollapsed, setIsCollapsed, hasProvider } = useSidebar();
    const [localSidebarOpen, setLocalSidebarOpen] = useState(true);
    const sidebarOpen = hasProvider ? !isCollapsed : localSidebarOpen;
    const setSidebarOpen = (open) => {
        if (hasProvider) {
            setIsCollapsed(!open);
        }
        setLocalSidebarOpen(open);
    };

    const [openMenus, setOpenMenus] = useState({});
    const navigate = useNavigate();
    const location = useLocation();

    // Menu items configuration
    const menuItems = [
        {
            label: 'Organizations',
            icon: Building2,
            path: '/platform/organizations',
        },
        {
            label: 'Tenant Setup',
            icon: Database,
            path: '/platform/tenant-setup',
        },
        // {
        //     label: 'User Management',
        //     icon: Users,
        //     path: '/platform/users',
        // }
    ];

    // Color scheme
    const colors = {
        gradient: 'from-[#f08804] to-[#e88b00]',
        active: 'bg-[#FFF4E6] text-[#C45500]',
        indicator: 'bg-[#232F3E]',
        avatar: 'bg-[#FFE8C8] text-[#C45500]'
    };

    // Toggle sidebar
    const handleToggle = () => {
        setSidebarOpen(!sidebarOpen);
    };

    // Handle menu item click
    const handleMenuClick = (path) => {
        navigate(path);
    };

    // Check if menu item is active
    const isActive = (path) => {
        return location.pathname.startsWith(path);
    };

    return (
        <aside
            className={`${sidebarOpen ? 'w-60' : 'w-20'
                } bg-white border-r border-slate-200 transition-all duration-300 flex flex-col shadow-sm flex-shrink-0`}
        >
            {/* Module Indicator Section */}
            <div className="border-b border-slate-200 p-4">
                <div className="flex items-center gap-3">
                    {/* Module Icon */}
                    <div className="w-10 h-10 bg-gradient-to-br from-[#f08804] to-[#e88b00] rounded-lg flex items-center justify-center shadow-sm">
                        <Shield className="w-5 h-5 text-white" />
                    </div>

                    {/* Module Name - only when expanded */}
                    {sidebarOpen && (
                        <div className="flex-1 min-w-0 text-left">
                            <h2 className="text-sm font-bold text-slate-900 leading-tight truncate">
                                Platform Admin
                            </h2>
                            <p className="text-[11px] text-slate-500 truncate">
                                Tenant Management
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Sidebar Toggle Button */}
            <div className="px-3 py-2 border-b border-slate-200">
                <button
                    onClick={handleToggle}
                    className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:ring-offset-1"
                    title={sidebarOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}
                    aria-label={sidebarOpen ? 'Collapse Sidebar' : 'Expand Sidebar'}
                >
                    {sidebarOpen ? (
                        <>
                            <PanelLeftClose className="w-5 h-5" />
                            <span className="text-sm font-medium">Collapse</span>
                        </>
                    ) : (
                        <PanelLeft className="w-5 h-5" />
                    )}
                </button>
            </div>

            {/* Menu Items Section */}
            <nav className="flex-1 p-3 space-y-1 overflow-y-auto custom-scrollbar">
                {menuItems.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.path);

                    return (
                        <button
                            key={item.path}
                            onClick={() => handleMenuClick(item.path)}
                            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all duration-200 group relative focus:outline-none focus:ring-2 focus:ring-[#f08804] focus:ring-offset-1 ${active
                                ? colors.active
                                : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                                }`}
                            title={!sidebarOpen ? item.label : ''}
                            aria-label={item.label}
                            aria-current={active ? 'page' : undefined}
                        >
                            {/* Active Indicator Bar */}
                            {active && (
                                <div className={`absolute left-0 top-1/2 -translate-y-1/2 w-1 h-8 ${colors.indicator} rounded-r-full`} />
                            )}

                            {/* Icon */}
                            <div className={`flex-shrink-0 ${active ? '' : 'group-hover:scale-110'} transition-transform`}>
                                <Icon className="w-5 h-5" />
                            </div>

                            {/* Label (when expanded) */}
                            {sidebarOpen && (
                                <>
                                    <span className="flex-1 text-left font-medium text-sm truncate">
                                        {item.label}
                                    </span>
                                    {active && <ChevronRight className="w-4 h-4 flex-shrink-0" />}
                                </>
                            )}
                        </button>
                    );
                })}
            </nav>

            {/* Powered By Section */}
            <div className="p-3 border-t border-slate-200">
                {sidebarOpen ? (
                    <div className="flex flex-col items-center gap-1">
                        <img
                            src="/loombiz-logo.png"
                            alt="Loombiz"
                            className="h-8 object-contain"
                        />
                        <span className="text-[10px] text-slate-400 mt-1">
                            eComm Notify v1.0
                        </span>
                    </div>
                ) : (
                    <div className="flex flex-col items-center gap-2">
                        <img
                            src="/loombiz-logo.png"
                            alt="Loombiz"
                            className="h-6 object-contain"
                            title="eComm Notify by Loombiz Consultancy"
                        />
                        <span className="text-[9px] text-slate-400" title="eComm Notify v1.0">
                            v1.0
                        </span>
                    </div>
                )}
            </div>
        </aside>
    );
}
